import { CheckCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ComparisonFeature {
  feature: string;
  neurosEdge: boolean | string;
  competitors: boolean | string;
}

interface FeatureComparisonProps {
  title?: string;
  subtitle?: string;
  features?: ComparisonFeature[];
  className?: string;
}

const defaultFeatures: ComparisonFeature[] = [
  {
    feature: "Real shell/API execution",
    neurosEdge: true,
    competitors: "❌ / limited"
  },
  {
    feature: "Live result interpretation",
    neurosEdge: true,
    competitors: false
  },
  {
    feature: "Human approval gate",
    neurosEdge: true,
    competitors: false
  },
  {
    feature: "Secure server automation",
    neurosEdge: true,
    competitors: false
  },
  {
    feature: "On-prem ready",
    neurosEdge: true,
    competitors: false
  },
  {
    feature: "Multi-agent readiness",
    neurosEdge: true,
    competitors: false
  },
  {
    feature: "Built-in IT/DevOps tooling",
    neurosEdge: true,
    competitors: false
  }
];

const FeatureIcon = ({ value }: { value: boolean | string }) => {
  if (value === true) {
    return (
      <div className="flex items-center justify-center">
        <span className="text-2xl text-green-400 drop-shadow-[0_0_8px_rgba(34,197,94,0.6)]">✅</span>
      </div>
    );
  } else if (value === false) {
    return (
      <div className="flex items-center justify-center">
        <span className="text-2xl text-red-400 drop-shadow-[0_0_8px_rgba(239,68,68,0.6)]">❌</span>
      </div>
    );
  } else {
    return (
      <div className="flex items-center justify-center">
        <span className="text-base font-medium text-gray-400">{value}</span>
      </div>
    );
  }
};

export function FeatureComparison({
  title = "How NeurosEdge Stands Apart",
  subtitle = "Discover the built-in power, automation, and infrastructure readiness that set NeurosEdge apart.",
  features = defaultFeatures,
  className
}: FeatureComparisonProps) {
  return (
    <section id="comparison" className={cn("py-24", className)} style={{ backgroundColor: '#0b0b0b' }}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Comparison Table */}
        <div className="max-w-5xl mx-auto animate-fade-in">
          <div className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden shadow-2xl backdrop-blur-sm">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-gray-800/50 border-b border-gray-700">
              <div className="p-6 text-left">
                <h3 className="text-lg font-semibold text-gray-400">Feature</h3>
              </div>
              <div className="p-6 text-center border-l border-gray-700">
                <h3 className="text-lg font-semibold text-white">NeurosEdge</h3>
              </div>
              <div className="p-6 text-center border-l border-gray-700">
                <h3 className="text-lg font-semibold text-gray-400">LangChain / AutoGPT / Copilot</h3>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-700">
              {features.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-3 hover:bg-gray-800/30 transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Feature Name */}
                  <div className="p-6 text-left">
                    <span className="text-white font-medium text-base">
                      {item.feature}
                    </span>
                  </div>

                  {/* NeurosEdge Column */}
                  <div className="p-6 text-center border-l border-gray-700 bg-gray-800/20">
                    <FeatureIcon value={item.neurosEdge} />
                  </div>

                  {/* Competitors Column */}
                  <div className="p-6 text-center border-l border-gray-700">
                    <FeatureIcon value={item.competitors} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 mt-8">
            {features.map((item, index) => (
              <div
                key={index}
                className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 animate-fade-in backdrop-blur-sm"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h4 className="text-white font-semibold mb-4 text-lg">
                  {item.feature}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-white font-medium mb-2">NeurosEdge</p>
                    <FeatureIcon value={item.neurosEdge} />
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 font-medium mb-2 text-sm">Others</p>
                    <FeatureIcon value={item.competitors} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>



        {/* Bottom CTA */}
        <div className="text-center mt-16 animate-fade-in">
          <p className="text-muted-foreground mb-6">
            Start adaptive automation today. Evolve operationally tomorrow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors duration-200">
              Get Early Access
            </button>
            <button className="px-8 py-3 border border-border hover:border-border/80 text-foreground hover:bg-muted font-semibold rounded-lg transition-colors duration-200">
              Learn More
            </button>
          </div>
        </div>


      </div>
    </section>
  );
}
